import {
  connectToggler,
  getSplitNames,
  getTreatments,
  initSplitSdk,
  mapIsFeatureOnToProps,
  selectTreatmentValue,
  selectTreatmentWithConfig,
} from '@splitsoftware/splitio-redux';

import * as Store from '../../../weborder/Store';
import { domainTag, FeatureFlag, featureFlags } from './featureFlags';

const componentToggle = (
  flagName: FeatureFlag,
  altComponent,
  defaultComponent,
) => connectToggler(`${domainTag}${flagName}`)(altComponent, defaultComponent);

const getDomainFeatureFlagList = (): string[] =>
  featureFlags.map((flagName) => `${domainTag}${flagName}`);

const getState = () => {
  const store = Store.getStoreInstance();

  return store?.getState();
};

const getFlagServiceStatus = (state?: WebOrder.State): FlagServiceStatus => {
  const retrievedState = state || getState();

  if (
    retrievedState?.splitio?.treatments &&
    Object.keys(retrievedState.splitio.treatments).length > 0
  ) {
    return 'READY';
  }

  if (
    retrievedState?.splitio.isTimedout === true ||
    retrievedState?.splitio.hasTimedout === true
  ) {
    return 'UNAVAILABLE';
  }

  return 'NOT_READY';
};

const getFlagValue = (
  flagName: FeatureFlag,
  state?: WebOrder.State,
): string => {
  const retrievedState = state || getState();

  return selectTreatmentValue(
    retrievedState.splitio,
    `${domainTag}${flagName}`,
  );
};

const getFlagValueConfig = (flagName: FeatureFlag, state?: WebOrder.State) => {
  const retrievedState = state || getState();

  return selectTreatmentWithConfig(
    retrievedState.splitio,
    `${domainTag}${flagName}`,
  );
};

const isFlagOn = (flagName: FeatureFlag, state?: WebOrder.State): boolean => {
  const retrievedState = state || getState();

  const result = mapIsFeatureOnToProps(`${domainTag}${flagName}`)(
    retrievedState,
  );
  return result.isFeatureOn;
};

const isFlagReady = (
  flagName: FeatureFlag,
  state?: WebOrder.State,
): boolean => {
  const retrievedState = state || getState();

  const result = selectTreatmentValue(
    retrievedState.splitio,
    `${domainTag}${flagName}`,
  );
  return result !== 'control';
};

const initialise = (
  store,
  featureFlagParams,
  enableDebugLogger = false,
): void => {
  const attributes = {
    countryCode: featureFlagParams.countryCode,
    whitelabelId: featureFlagParams.whitelabelId,
  };

  // startup limits are in seconds
  const sdkBrowserConfig = {
    core: {
      authorizationKey: process.env.REACT_APP_SPLIT_OAUTH_KEY,
      key: featureFlagParams.deviceId,
      trafficType: 'AnonymousUser',
    },
    debug: false,
    startup: {
      requestTimeoutBeforeReady: 5,
      retriesOnFailureBeforeReady: 1,
      readyTimeout: 5,
      eventsFirstPushWindow: 10,
    },
    streamingEnabled: true,
    sync: {
      splitFilters: [
        {
          type: 'byName',
          values: getDomainFeatureFlagList(),
        },
      ],
    },
  };

  if (enableDebugLogger === true) {
    sdkBrowserConfig.debug = true;
  }

  store.dispatch(initSplitSdk({ config: sdkBrowserConfig })).then(() => {
    const splitNames = getSplitNames();
    store.dispatch(getTreatments({ splitNames, attributes }));
  });
};

export type FlagServiceStatus = 'NOT_READY' | 'READY' | 'UNAVAILABLE';

export const flagService = {
  componentToggle,
  getDomainFeatureFlagList,
  getFlagServiceStatus,
  getFlagValue,
  getFlagValueConfig,
  isFlagOn,
  isFlagReady,
  initialise,
};
